import React, { useState, useCallback, useRef, useEffect } from 'react'
import AddPresetDialog from '../../AddPresetDialog.jsx'
import DeletePresetDialog from '../../DeletePresetDialog.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DivButton from 'GeneralUI/DivButton/DivButton.jsx'
import { openModal } from 'actions/modalActions'
import { xtractAPI } from 'actions/apiActions'
import { useDispatch } from 'react-redux'
import _ from 'lodash'

const TablePresetsButton = ({visible, userStateKey, defaultSettings, marshalSettings, restoreState}) => {
  const [presetOpen, setPresetOpen] = useState(false)
  const [presets, setPresets] = useState([])
  const dispatch = useDispatch()

  /// //////////////////////////////////////////
  //
  //  THIS IS WRONG AND NEEDS TO BE FIXED - the lead-in ('tp') is silly short
  //
  //  Only this way to accommodate the 32 char limit on section names in config table
  //
  //  And when it gets fixed - any presets in existing databases will need to be migrated
  //
  /// //////////////////////////////////////////

  const presetKey =
    userStateKey
      ? 'tp' + ':' + userStateKey.substring(userStateKey.length - 28)
      : null

  useEffect(() => {
    getXtractTablePresets(userStateKey).then(setPresets)
  },[])

  const useOutsideClick = (callback) => {
    const ref = useRef()

    useEffect(() => {
      const handleClick = (event) => {
        callback()
      }

      document.addEventListener('click', handleClick)

      return () => {
        document.removeEventListener('click', handleClick)
      }
    }, [callback])

    return ref
  }

  const getXtractTablePresets = useCallback((userStateKey) => {
    return new Promise((resolve, reject) => {
      if (!userStateKey) {
        resolve(defaultSettings)
      } else {
        const callbacks = {
          200: response => {
            const { matches } = response.data.pagination

            if (matches === 0) {
              resolve([])
            } else {
              const settings = response.data.data.config
                .map(config => {
                  try {
                    let settingsString = config.value
                    // if the value doesn't start with an open brace, it is base64-encoded (new-style)
                    // otherwise it is just JSON (old-style)
                    if (settingsString.length > 0 && settingsString[0] !== '{') {
                      // decode the base64
                      settingsString = window.atob(settingsString)
                    }
                    const settings = JSON.parse(settingsString)
                    return ({
                      name: config.name,
                      settings: {
                        ...defaultSettings,
                        ...settings
                      }
                    })
                  } catch {
                    return null
                  }
                })
                .filter(setting => setting)

              resolve(settings)
            }
          }
        }

        const searchCriteria =
          { equal: { section: presetKey } }

        dispatch(xtractAPI('config', 'search', searchCriteria, callbacks))
      }
    })
  }, [defaultSettings, presetKey])

  const saveXtractTablePreset = useCallback((userStateKey, presetName, state) => {
    const callbacks = {
      200: response => {
        const { matches } = response.data.pagination

        const callbacks = {
          200: response => {
            getXtractTablePresets(userStateKey).then(setPresets)
          }
        }

        if (matches > 0 && (!state || (typeof state === 'object' && Object.keys(state).length === 0))) {
          // its a delete
          const configId = response.data.data.config[0].config_id
          dispatch(xtractAPI(`config/${configId}`, 'delete', null, callbacks))
        } else {
          const {
            showFilters,
            sorted,
            pageSize,
            columnSizing,
            filtered,
            selectedColumns
          } = state

          const presetState = {
            showFilters,
            sorted,
            pageSize,
            columnSizing,
            filtered: showFilters ? filtered : [],
            selectedColumns
          }

          // encode as base64 to fix #8998
          const presetStateJson = window.btoa(JSON.stringify(presetState))
          const data = {
            app: 'ALL',
            section: presetKey,
            name: presetName,
            value: presetStateJson
          }

          if (matches === 0) {
            // its a create
            dispatch(xtractAPI('config', 'create', data, callbacks))
          } else {
            // its an update
            const configId = response.data.data.config[0].config_id
            dispatch(xtractAPI(`config/${configId}`, 'update', data, callbacks))
          }
        }
      }
    }

    const searchCriteria =
      {
        and: [
          { equal: { section: presetKey } },
          { equal: { name: presetName } }
        ]
      }

    dispatch(xtractAPI('config', 'search', searchCriteria, callbacks))
  }, [presetKey, getXtractTablePresets])

  const saveStateToPreset = useCallback(presetName => {
    saveXtractTablePreset(
      userStateKey,
      presetName,
      marshalSettings()
    )
  }, [saveXtractTablePreset, userStateKey, marshalSettings])

  const getStateFromPreset = useCallback(presetName => {
    const preset = presets.find(
      preset => preset.name === presetName
    )

    if (preset) {
      restoreState(preset.settings)
    }
  }, [presets, restoreState])

  const addPreset = useCallback(() => {
    dispatch(
      openModal('add_preset', {
        title: 'Add preset',
        content: (
          <AddPresetDialog
            okHandler={presetName => {
              saveStateToPreset(presetName)
            }}
          />
        ),
        closeable: true
      })
    )
  }, [openModal, saveStateToPreset])

  const deletePreset = useCallback(() => {
    dispatch(
      openModal('delete_preset', {
        title: 'Delete Preset',
        content: (
          <DeletePresetDialog
            presets={presets}
            okHandler={presetName => {
              const newPresets = presets.slice()
              const presetIndex = newPresets.findIndex(
                preset => preset.name === presetName
              )

              saveXtractTablePreset(
                userStateKey,
                presetName,
                null
              )

              if (presetIndex >= 0) {
                newPresets.splice(presetIndex, 1)
              }
              setPresets(newPresets)
            }}
          />
        ),
        closeable: true
      })
    )
  }, [openModal, presets, saveXtractTablePreset, userStateKey])

  const handleClickOutside = useCallback(() => {
    setPresetOpen(false)
  }, [])

  const presetDropdownRef = useOutsideClick(handleClickOutside)

  const presetsButton = () => {
    const optionsClass = presetOpen ? '' : 'hidden'
    const options = presets.map(
      preset => preset.name
    )

    return (
      <div>
        <DivButton
          className={'btn btn-default control-button'}
          data-toggle={'tooltip'}
          data-title={'Presets'}
          aria-label={'presets button'}
          onClick={(event) => {
            event.stopPropagation()
            setPresetOpen(!presetOpen)
          }}
        >
          <FontAwesomeIcon icon={['fas', 'bars']} size="sm" fixedWidth />
        </DivButton>

        <div ref={presetDropdownRef} className={'options-container ' + optionsClass}>
          <button
            key={'-1'}
            onClick={() => {
              addPreset()
              setPresetOpen(false)
            }}
          >
            Save preset...
          </button>
          <button
            key={'-2'}
            onClick={() => {
              deletePreset()
              setPresetOpen(false)
            }}
          >
            Delete preset...
          </button>
          {
            options.map((label, index) => {
              return (
                <button
                  key={index}
                  onClick={() => {
                    getStateFromPreset(label)
                    setPresetOpen(false)
                  }}
                >
                  {label}
                </button>
              )
            })
          }
        </div>
      </div>
    )
  }

  return userStateKey && visible ? presetsButton() : null
}

export default TablePresetsButton